import type { MarketRegionResponseDto } from "Infrastructure/Api/Api";
import { useHandleServerError } from "Infrastructure/Utilities/Forms/Hooks/useHandleServerError";
import { useUpdateRegionMutation } from "Market/Api/Mutation/updateRegionMutation";
import { useDeleteRegionTransportMutation } from "Market/Api/Mutation/updateRegionTransportMutation";
import {
	getRegionListItemFormSchema,
	type RegionListItemFormSchema,
} from "Market/Components/Form/Schema/regionListItemFormSchema";
import { DeleteRegionConfirmationModal } from "Market/Components/Modals/DeleteRegionConfirmationModal";
import { EditRegionModal } from "Market/Components/Modals/EditRegionModal";
import { RegionJsonEditorModal } from "Market/Components/Modals/RegionJsonEditorModal";
import Input from "Shared/Components/Form/CustomFormComponent/Input";
import { StartAdornment } from "Shared/Components/Inputs/Adornments/StartAdornment";
import { zodResolver } from "@hookform/resolvers/zod";
import { Delete, Edit, LocationOn, Refresh } from "@mui/icons-material";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { useForm } from "react-hook-form";

type UpdateRegionFormProps = {
	region: MarketRegionResponseDto;
};

export const RegionListItemForm = ({ region }: UpdateRegionFormProps) => {
	const [isPendingDelete, setIsPendingDelete] = useState<boolean>(false);
	const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] =
		useState<boolean>(false);
	const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
	const [isJsonEditorOpen, setIsJsonEditorOpen] = useState<boolean>(false);

	const form = useForm<RegionListItemFormSchema>({
		resolver: zodResolver(getRegionListItemFormSchema()),
		defaultValues: region,
	});

	const {
		control,
		formState: { errors },
	} = form;

	const { mutate: updateRegion, isPending: isPendingUpdate } =
		useUpdateRegionMutation(region.marketId, region.id);
	const { mutate: deleteRegionTransport, isPending: isPendingCityDelete } =
		useDeleteRegionTransportMutation(region.marketId, region.regionCode);

	const { handleServerError } = useHandleServerError(form);

	const handleRegionDelete = () => {
		setIsPendingDelete(true);
		deleteRegionTransport(undefined, {
			onSuccess: () => {
				handleServerError(() =>
					updateRegion(
						{
							isDeleted: true,
						},
						{
							onSettled: () => setIsPendingDelete(false),
						},
					),
				);
			},
			onError: () => setIsPendingDelete(false),
		});
	};

	const handleRegionActivation = () => {
		setIsPendingDelete(true);
		handleServerError(() =>
			updateRegion({
				isDeleted: false,
			}),
		);
		setIsPendingDelete(false);
	};

	const formValues = form.watch();

	return (
		<>
			<Stack flexDirection="row" gap={2} alignItems="flex-end">
				<Box width="100%">
					<Input
						id="name"
						name="name"
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
						disabled
						startAdornment={
							<StartAdornment>
								<Typography>{region.id}</Typography>
							</StartAdornment>
						}
					/>
				</Box>
				<Stack gap={1} direction="row">
					{!region.isDeleted && (
						<IconButton
							color="warning"
							loading={isPendingDelete || isPendingCityDelete}
							disabled={!formValues.name}
							onClick={() => setIsDeleteConfirmOpen(true)}
							sx={{ background: "none !important" }}
						>
							<Delete />
						</IconButton>
					)}

					{!region.isDeleted && (
						<IconButton
							disabled={!formValues.name}
							sx={{ background: "none !important" }}
							onClick={() => setIsEditModalOpen(true)}
						>
							<Edit />
						</IconButton>
					)}

					{!region.isDeleted && (
						<IconButton
							disabled={!formValues.name}
							sx={{ background: "none !important" }}
							onClick={() => setIsJsonEditorOpen(true)}
						>
							<LocationOn />
						</IconButton>
					)}

					{region.isDeleted && (
						<IconButton
							color="warning"
							loading={isPendingUpdate}
							disabled={!formValues.name}
							onClick={handleRegionActivation}
							sx={{
								background: "none !important",
								transform: "rotate(-180deg)",
								transition: "transform 0.2s ease-in-out",
								":hover": {
									transform: "rotate(-360deg)",
								},
							}}
						>
							<Refresh />
						</IconButton>
					)}
				</Stack>
			</Stack>
			<DeleteRegionConfirmationModal
				isOpen={isDeleteConfirmOpen}
				onClose={() => setIsDeleteConfirmOpen(false)}
				onConfirm={handleRegionDelete}
				isPending={isPendingDelete || isPendingCityDelete}
			/>
			<EditRegionModal
				isOpen={isEditModalOpen}
				setIsOpen={setIsEditModalOpen}
				region={region}
			/>
			{isJsonEditorOpen && (
				<RegionJsonEditorModal
					isOpen={isJsonEditorOpen}
					setIsOpen={setIsJsonEditorOpen}
					region={region}
				/>
			)}
		</>
	);
};
